/**
 * royaleScoring.ts
 *
 * Pure scoring + ranking logic for Puzzle Royale. No React, no store, no
 * socket — unit-testable in isolation. This is the SHARED SPEC: the
 * cc-game-service handler (funPuzzleRoyale.js) mirrors `applyMove` and
 * `rankLeaderboard` exactly so client and server never disagree. The server
 * broadcast is the source of truth at runtime; this module backfills combo/
 * points for optimistic display and rejoin reconstruction when the server
 * echo is unavailable (older builds).
 *
 * Rules (PRD — updated 2026-06-14):
 *   - Solve = +1 point.
 *   - PLUS a per-solve streak bonus equal to the player's CURRENT combo tier
 *     (the bonus applies on EVERY consecutive solve while the streak is up, not
 *     just once at a milestone): combo 3–4 → +1, 5–9 → +2, 10–14 → +3,
 *     15+ → +5 (STREAK_LADDER = the tier breakpoints + their bonus).
 *     e.g. C3 +1, C4 +1, C5 +2, C6 +2, …
 *   - A wrong solve resets the combo to 0 and earns no point (you still
 *     advance to the next puzzle).
 *   - Rank by points desc, tie-break = first to reach that point total.
 *   - Piece tier from PIECE_BANDS. King is the leader badge (resolveKing).
 */

import {KING_ENABLED_AT, PIECE_BANDS, STREAK_LADDER} from '../royaleConstants';
import type {PieceTier, PlayerRoyaleState} from '../royaleTypes';

/** The mutable scoring fields a move updates. */
export interface ScoreState {
  points: number;
  combo: number;
  bestCombo: number;
}

/**
 * Apply one move to a player's score state. Returns a NEW state (no mutation).
 *
 * @param prev    the player's current {points, combo, bestCombo}
 * @param solved  true if the puzzle was solved correctly
 */
export function applyMove(prev: ScoreState, solved: boolean): ScoreState {
  if (!solved) {
    // Wrong solve: no point, streak breaks. bestCombo is preserved.
    return {points: prev.points, combo: 0, bestCombo: prev.bestCombo};
  }
  const combo = prev.combo + 1;
  // Base +1 for the solve, PLUS the per-solve bonus for the current combo tier.
  const points = prev.points + 1 + comboTierBonus(combo);
  const bestCombo = combo > prev.bestCombo ? combo : prev.bestCombo;
  return {points, combo, bestCombo};
}

/**
 * The per-solve streak bonus for a solve made at combo value `combo`: the
 * bonus of the HIGHEST ladder tier whose threshold ≤ combo (0 below the first
 * tier). e.g. combo 3–4 → 1, 5–9 → 2, 10–14 → 3, 15+ → 5.
 */
export function comboTierBonus(combo: number): number {
  let bonus = 0;
  for (const m of STREAK_LADDER) {
    if (combo >= m.at) {
      bonus = m.bonus;
    }
  }
  return bonus;
}

/**
 * Total streak bonus a run of `combo` consecutive solves has earned (the SUM
 * of the per-solve tier bonus at each step 1..combo). Used to reconstruct a
 * player's points from a response trail on rejoin.
 */
export function bonusForCombo(combo: number): number {
  let sum = 0;
  for (let c = 1; c <= combo; c++) {
    sum += comboTierBonus(c);
  }
  return sum;
}

/** Map a point total to its piece tier (highest band whose min ≤ points). */
export function pieceForPoints(points: number): PieceTier {
  let tier: PieceTier = PIECE_BANDS[0].tier;
  for (const band of PIECE_BANDS) {
    if (points >= band.min) {
      tier = band.tier;
    }
  }
  return tier;
}

/**
 * Rank players: points desc, then `reachedAt` asc (first-to-reach wins ties),
 * then userId asc for total determinism. Returns an array of userIds, winner
 * first. Eliminated players are still ranked (they keep their final points).
 */
export function rankLeaderboard(
  players: Record<string, PlayerRoyaleState>,
): string[] {
  return Object.values(players)
    .slice()
    .sort((a, b) => {
      // Eliminated/forfeited players rank BELOW everyone still in, regardless
      // of points — mirrors the server's calculateWinner so client + server
      // agree (a lone survivor with fewer points still outranks a forfeiter who
      // led when they quit). Fixes the end leaderboard + "your rank" showing a
      // forfeiter as #1.
      const aOut = a.eliminated ? 1 : 0;
      const bOut = b.eliminated ? 1 : 0;
      if (aOut !== bOut) {
        return aOut - bOut;
      }
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      if (a.reachedAt !== b.reachedAt) {
        return a.reachedAt - b.reachedAt;
      }
      return a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0;
    })
    .map(p => p.userId);
}

/**
 * Resolve the King (leader) badge. Returns the leader's userId, or null if no
 * player has yet reached KING_ENABLED_AT (the crown stays dark until then).
 * The leader is rank-1 by the same ordering as rankLeaderboard.
 */
export function resolveKing(
  players: Record<string, PlayerRoyaleState>,
): string | null {
  const ranked = rankLeaderboard(players);
  if (ranked.length === 0) {
    return null;
  }
  const topId = ranked[0];
  const top = players[topId];
  if (!top || top.points < KING_ENABLED_AT) {
    return null;
  }
  return topId;
}

/**
 * Recompute the derived fields (piece, isLeader) across all players after any
 * score change. Mutates a shallow copy and returns it. The store calls this
 * after every applyMoveBroadcast so the leaderboard + King are always current.
 */
export function recomputeDerived(
  players: Record<string, PlayerRoyaleState>,
): Record<string, PlayerRoyaleState> {
  const kingId = resolveKing(players);
  const next: Record<string, PlayerRoyaleState> = {};
  for (const [id, p] of Object.entries(players)) {
    next[id] = {
      ...p,
      piece: pieceForPoints(p.points),
      isLeader: id === kingId,
    };
  }
  return next;
}
